import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Music4, Menu, X, Sparkles } from "lucide-react";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Generator", to: "/generator" },
  { label: "Guides", to: "/guides" },
  { label: "FAQ", to: "/faq" },
];

export function OpenGeneratorButton({ testId = "open-generator-button", className = "", label = "Open Song Generator" }) {
  return (
    <Link
      to="/generator"
      data-testid={testId}
      className={`inline-flex items-center justify-center gap-2 rounded-full font-bold bg-gradient-to-r from-[#E6C66E] to-[#D4AF37] text-[#0B0E14] px-5 h-11 shadow-[0_0_24px_rgba(212,175,55,0.3)] hover:opacity-90 transition-opacity ${className}`}
    >
      <Sparkles className="w-4 h-4" />
      {label}
    </Link>
  );
}

function PublicHeader() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const active = (to) => location.pathname === to;

  return (
    <header className="sticky top-0 z-40 border-b border-[#1E2C48] bg-[#070B14]/90 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to="/" data-testid="brand-home-link" className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#E6C66E] to-[#D4AF37] flex items-center justify-center shadow-[0_0_20px_rgba(212,175,55,0.35)]">
            <Music4 className="w-5 h-5 text-[#0B0E14]" strokeWidth={2.5} />
          </div>
          <span className="font-heading text-base sm:text-lg font-extrabold tracking-tight text-slate-50">
            AI Song Prompt Studio
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-7">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={`text-sm font-medium transition-colors ${
                active(n.to) ? "text-[#22D3EE]" : "text-slate-300 hover:text-slate-50"
              }`}
            >
              {n.label}
            </Link>
          ))}
          <OpenGeneratorButton testId="header-open-generator" />
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden text-slate-200 p-2"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-[#1E2C48] bg-[#070B14] px-4 py-4 space-y-3">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              onClick={() => setOpen(false)}
              data-testid={`mobile-nav-${n.label.toLowerCase()}`}
              className={`block text-base font-medium py-1 ${
                active(n.to) ? "text-[#22D3EE]" : "text-slate-200"
              }`}
            >
              {n.label}
            </Link>
          ))}
          <OpenGeneratorButton testId="mobile-open-generator" className="w-full" />
        </div>
      )}
    </header>
  );
}

function PublicFooter() {
  return (
    <footer className="border-t border-[#1E2C48] bg-[#0D1424] mt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 grid grid-cols-1 sm:grid-cols-3 gap-8">
        <div>
          <div className="flex items-center gap-2">
            <Music4 className="w-5 h-5 text-[#D4AF37]" />
            <span className="font-heading font-bold text-slate-100">AI Song Prompt Studio</span>
          </div>
          <p className="mt-3 text-sm text-slate-400 leading-relaxed max-w-xs">
            An independent tool for preparing song inputs — a title, a Suno Styles prompt, and complete
            original lyrics — to paste into Suno. Not affiliated with or endorsed by Suno.
          </p>
        </div>
        <div>
          <h3 className="font-label text-amber-400/90 mb-3">Explore</h3>
          <ul className="space-y-2 text-sm">
            {NAV.map((n) => (
              <li key={n.to}>
                <Link to={n.to} className="text-slate-300 hover:text-[#22D3EE] transition-colors">
                  {n.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h3 className="font-label text-amber-400/90 mb-3">Get started</h3>
          <p className="text-sm text-slate-400 mb-3">
            Ready to write your next song prompt? Open the generator.
          </p>
          <OpenGeneratorButton testId="footer-open-generator" />
        </div>
      </div>
      <div className="border-t border-[#1E2C48] py-4 text-center text-xs text-slate-500">
        © {new Date().getFullYear()} AI Song Prompt Studio · Independent tool, not affiliated with Suno or OpenAI.
      </div>
    </footer>
  );
}

export function PublicLayout({ children }) {
  return (
    <div className="min-h-screen bg-[#070B14] text-slate-200 flex flex-col">
      <PublicHeader />
      <main className="flex-1">{children}</main>
      <PublicFooter />
    </div>
  );
}
